import { Grid, InputAdornment } from "@mui/material";
import React, {
  Dispatch,
  FC,
  SetStateAction,
  SyntheticEvent,
  useCallback,
} from "react";
import {
  FoodItem,
  IngredientState,
  StorageTemperature,
} from "client/jspPlatformExperiment";
import { useUpdateOnBlur } from "hooks/common/useUpdateOnBlur";
import SpacedTextInput, {
  SpacedAutocompleteInput,
} from "components/common/ui/SpacedTextInput";
import TimeDurationField from "components/common/ui/TimeDurationField";
import dayjs from "dayjs";
import { Duration } from "dayjs/plugin/duration";
import { ingredientCategoriesOptions } from "utils/food/foodItemOptionDisplayUtils";

interface FoodItemEditSectionProps {
  foodItem: FoodItem;
  setFoodItem: Dispatch<SetStateAction<FoodItem>>;
}

const FoodItemBasicInfoEditSection: FC<FoodItemEditSectionProps> = ({
  foodItem,
  setFoodItem,
}) => {
  const [displayFoodName, handleNameChange, handleNameBlur] = useUpdateOnBlur(
    foodItem.food_name,
    (value) => setFoodItem((prev) => ({ ...prev, food_name: value })),
  );

  const [displayPortionSize, handlePortionSizeChange, handlePortionSizeBlur] =
    useUpdateOnBlur<number | null>(
      foodItem.portion_size ?? null,
      (value) => setFoodItem((prev) => ({ ...prev, portion_size: value })),
      {
        toDisplay: (v) => (v == null ? "" : String(v)),
        toSubmit: (v) => (v === "" ? null : Number(v)),
      },
    );

  const handleStateChange = useCallback(
    (_: SyntheticEvent, newValue: IngredientState | null) => {
      setFoodItem({
        ...foodItem,
        state: newValue !== null ? newValue : IngredientState.NONE,
      });
    },
    [foodItem, setFoodItem],
  );

  const handleCategoryChange = useCallback(
    (_: SyntheticEvent, newValue: string | null) => {
      setFoodItem({
        ...foodItem,
        category: newValue !== null ? newValue : "other",
      });
    },
    [foodItem, setFoodItem],
  );

  const handleStoreTempChange = useCallback(
    (_: SyntheticEvent, newValue: StorageTemperature | null) => {
      setFoodItem({
        ...foodItem,
        storage_temp: newValue,
      });
    },
    [foodItem, setFoodItem],
  );

  /**
   * Handle shelf life change
   */
  const handleShelfLifeChange = useCallback(
    (newShelfLife: Duration) => {
      setFoodItem({
        ...foodItem,
        shelf_life: newShelfLife.toISOString(),
      });
    },
    [foodItem, setFoodItem],
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <SpacedTextInput
          label="Name"
          value={displayFoodName}
          onChange={handleNameChange}
          onBlur={handleNameBlur}
          required
        />
      </Grid>
      <Grid item xs={6} md={3} lg={2}>
        <SpacedAutocompleteInput
          label="State"
          value={foodItem.state}
          options={Object.values(IngredientState)}
          onChange={handleStateChange}
        />
      </Grid>
      <Grid item xs={6} md={3} lg={2}>
        <SpacedAutocompleteInput
          label="Category"
          value={foodItem.category}
          options={ingredientCategoriesOptions}
          onChange={handleCategoryChange}
        />
      </Grid>
      <Grid item xs={6} md={3} lg={2}>
        <SpacedAutocompleteInput
          label="Store Temperature"
          value={foodItem.storage_temp}
          options={Object.values(StorageTemperature)}
          onChange={handleStoreTempChange}
        />
      </Grid>
      <Grid item xs={6} md={3} lg={2}>
        <SpacedTextInput
          label="Portion Size"
          value={displayPortionSize}
          onChange={handlePortionSizeChange}
          onBlur={handlePortionSizeBlur}
          type="number"
          InputProps={{
            endAdornment: <InputAdornment position="end">g</InputAdornment>,
          }}
          error={
            displayPortionSize !== "" ? Number(displayPortionSize) <= 0 : false
          }
        />
      </Grid>
      <Grid item xs={12} md={6} lg={4}>
        <TimeDurationField
          label="Shelf life"
          value={dayjs.duration(foodItem.shelf_life)}
          onChange={handleShelfLifeChange}
        />
      </Grid>
    </Grid>
  );
};

export default FoodItemBasicInfoEditSection;
